"use client";

import React, { useEffect } from 'react';

/**
 * Fonction utilitaire pour supprimer les attributs Bitwarden qui causent des erreurs d'hydratation
 * Cette fonction est appelée côté client pour nettoyer le DOM
 */
export function suppressBitwarden() {
  if (typeof window === 'undefined') return;

  // Supprimer immédiatement tous les attributs bis_skin_checked existants
  const removeAllBitwardenAttributes = () => {
    const elements = document.querySelectorAll('[bis_skin_checked]');
    elements.forEach(element => {
      element.removeAttribute('bis_skin_checked');
    });
  };

  // Nettoyage initial immédiat
  removeAllBitwardenAttributes();

  // Observer continu pour intercepter les nouvelles additions
  const observer = new MutationObserver((mutations) => {
    let needsCleanup = false;
    
    mutations.forEach((mutation) => {
      // Vérifier les modifications d'attributs
      if (mutation.type === 'attributes' && mutation.attributeName === 'bis_skin_checked') {
        const target = mutation.target as Element;
        target.removeAttribute('bis_skin_checked');
        needsCleanup = true;
      }
      
      // Vérifier les nouveaux éléments ajoutés
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            // Vérifier l'élément lui-même
            if (element.hasAttribute('bis_skin_checked')) {
              element.removeAttribute('bis_skin_checked');
              needsCleanup = true;
            }
            // Vérifier ses descendants
            const descendantsWithAttribute = element.querySelectorAll('[bis_skin_checked]');
            if (descendantsWithAttribute.length > 0) {
              descendantsWithAttribute.forEach(desc => desc.removeAttribute('bis_skin_checked'));
              needsCleanup = true;
            }
          }
        });
      }
    });
    
    // Nettoyage supplémentaire si nécessaire
    if (needsCleanup) {
      setTimeout(removeAllBitwardenAttributes, 10);
    }
  });

  // Observer tout le document avec toutes les options
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['bis_skin_checked'],
    childList: true,
    subtree: true,
    characterData: false
  });

  // Nettoyage périodique pour être sûr
  const intervalId = setInterval(removeAllBitwardenAttributes, 1000);

  // Fonction de nettoyage
  return () => {
    observer.disconnect();
    clearInterval(intervalId);
  };
}

/**
 * Hook React pour utiliser la suppression des attributs Bitwarden
 * À utiliser dans les composants qui ont des erreurs d'hydratation
 */
export function useSuppressBitwarden() {
  useEffect(() => {
    const cleanup = suppressBitwarden();
    return cleanup;
  }, []);
}

/**
 * Fonction pour nettoyer spécifiquement un élément et ses enfants
 */
export function cleanElementBitwarden(element: Element) {
  if (!element) return;
  
  // Nettoyer l'élément lui-même
  if (element.hasAttribute('bis_skin_checked')) {
    element.removeAttribute('bis_skin_checked');
  }
  
  // Nettoyer tous ses descendants
  const descendantsWithAttribute = element.querySelectorAll('[bis_skin_checked]');
  descendantsWithAttribute.forEach(desc => desc.removeAttribute('bis_skin_checked'));
}

/**
 * Middleware pour nettoyer les attributs avant le rendu React
 */
export function withBitwardenSuppression<T extends React.ComponentType<any>>(
  Component: T
): T {
  const WrappedComponent = (props: any) => {
    useSuppressBitwarden();
    return React.createElement(Component, props);
  };
  
  WrappedComponent.displayName = `withBitwardenSuppression(${Component.displayName || Component.name})`;
  
  return WrappedComponent as T;
} 